import {
  ReactNode,
  useRef,
  useState,
  MouseEvent,
  CSSProperties,
  memo,
  MutableRefObject,
} from 'react';
import { createPortal } from 'react-dom';

import { cx } from '@libs/classnames';

import styles from './AnimatedNode.module.css';

export type Props = {
  tag?: React.ElementType;
  animation?:
    | 'opacity'
    | 'half-opacity'
    | 'scale'
    | 'none'
    | 'bounce-up'
    // появление с масштабом, уход прозрачностью
    | 'scale-fade'
    | 'change-table'
    | 'crossfade-synced';
  className?: string;
  persist?: boolean;
  initial?: boolean;
  portal?: Element;
  children?: ReactNode;
  attributes?: object;
  style?: CSSProperties;
  nodeRef?: MutableRefObject<HTMLElement | null>;
  onClick?: (e: MouseEvent) => void;
};

const IDENTITY = (e: ReactNode) => e;

export const AnimatedNode = memo(function AnimatedNode({
  tag,
  animation = 'opacity',
  className,
  persist,
  initial,
  portal,
  children,
  attributes,
  style,
  nodeRef,
  onClick,
}: Props) {
  const disabled = animation === 'none';
  const prevChildren = useRef(initial ? undefined : children);
  const memoizedChildren = useRef(children);
  const enterRef = useRef(false);
  const animating = useRef(false);
  const [, forceUpdate] = useState(false);

  const hasChildren = children || children === 0;

  if (!disabled && hasChildren && !prevChildren.current) {
    enterRef.current = true;
  }
  if (!hasChildren) {
    enterRef.current = false;
  }

  const leave = Boolean(!disabled && prevChildren.current && !hasChildren);

  if (hasChildren) {
    memoizedChildren.current = children;
  }
  prevChildren.current = children;

  const HtmlTagname = tag ?? 'div';

  const wrap = portal ? (el: ReactNode) => createPortal(el, portal) : IDENTITY;

  if (!hasChildren && !leave && !animating.current) {
    if (persist) {
      return wrap(
        <HtmlTagname className={className} style={style} onClick={onClick} {...attributes} />,
      );
    }
    return null;
  }

  const content = hasChildren ? children : memoizedChildren.current;
  const hidden = leave || (animating.current && !hasChildren);

  const handleAnimationStart = (e: React.AnimationEvent) => {
    if (e.animationName.includes('anim-')) {
      animating.current = true;
      e.target?.addEventListener('animationcancel', handleAnimationCancel);
    }
  };
  const handleAnimationEnd = (e: React.AnimationEvent) => {
    if (e.animationName.includes('anim-')) {
      animating.current = false;
      if (!hasChildren) {
        forceUpdate((v) => !v);
      }
    }
  };
  const handleAnimationCancel = (e: Event) => {
    handleAnimationEnd(e as unknown as React.AnimationEvent);
    e.target?.removeEventListener('animationcancel', handleAnimationCancel);
  };

  return wrap(
    <HtmlTagname
      className={cx(className, {
        [styles.enter]: enterRef.current,
        [styles.leave]: leave,
      })}
      style={style}
      onClick={onClick}
      onAnimationStart={handleAnimationStart}
      onAnimationEnd={handleAnimationEnd}
      data-animation={animation}
      aria-hidden={hidden}
      inert={hidden}
      {...({ ref: nodeRef } as object)}
      {...attributes}
    >
      {content}
    </HtmlTagname>,
  );
});

export type NodeInfo = {
  key: string | number;
} & Props;
export type NodesProps = {
  children?: Array<NodeInfo | null | undefined | false> | null | false;
  // узлы, появившиеся после первого рендера, анимируют вход; без флага вход решает
  // initial каждого узла
  appear?: boolean;
  // ушедший узел убрать сразу, без анимации ухода: например, он переехал в соседний список
  // и анимирует вход там
  skipLeave?: (key: string | number) => boolean;
};

// Сколько держим ушедший узел: дольше любой анимации ухода
const LEAVE_MS = 1000;

type Ghost = { props: NodeInfo; index: number; left: number };

// Список узлов с анимацией появления и ухода. Ушедший узел доигрывает уход на своем прежнем
// месте и переживает любые перерисовки родителя, пока не истечет LEAVE_MS или не вернется.
// Порядок в DOM не меняем: перенос узла сбрасывает идущие у него переходы и анимации. Слой
// ушедшего (выше или ниже соседей) — забота владельца: у ушедшего aria-hidden="true"
export function AnimatedDynamicNodes({ children, appear, skipLeave }: NodesProps) {
  const prevRef = useRef<NodeInfo[] | null>(null);
  const ghostsRef = useRef(new Map<string | number, Ghost>());
  const ghosts = ghostsRef.current;
  const now = Date.now();

  const nodes = (children || []).filter((n): n is NodeInfo => !!n);
  const present = new Set(nodes.map((n) => n.key));

  prevRef.current?.forEach((props, index) => {
    if (!present.has(props.key) && !ghosts.has(props.key) && !skipLeave?.(props.key)) {
      ghosts.set(props.key, { props, index, left: now });
    }
  });
  ghosts.forEach((g, key) => {
    if (present.has(key) || now - g.left > LEAVE_MS) ghosts.delete(key);
  });

  const before = prevRef.current;
  const items = nodes.map((props) => ({
    props:
      appear && before && !before.some((b) => b.key === props.key)
        ? { ...props, initial: true }
        : props,
    leaving: false,
  }));
  [...ghosts.values()]
    .sort((x, y) => x.index - y.index)
    .forEach((g) => {
      items.splice(Math.min(g.index, items.length), 0, { props: g.props, leaving: true });
    });

  prevRef.current = nodes;

  return (
    <>
      {items.map(({ props, leaving }) =>
        leaving ? (
          // eslint-disable-next-line react/no-children-prop
          <AnimatedNode {...props} key={props.key} children={null} />
        ) : (
          <AnimatedNode {...props} key={props.key} />
        ),
      )}
    </>
  );
}
